"use client";

import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { useRef, useState, useEffect } from "react";
import { LayoutDashboard } from "lucide-react";
import { createClient } from "@/lib/supabase/clients";
import { LanguageSelector } from "@/components/ui/LanguageSelector";
import { useHashScrollOnNavigation } from "@/hooks/useHashScrollOnNavigation";
import styles from "./Header.module.css";
import Logo from "./Logo";

// Section IDs for navigation
const SECTIONS = ['how-it-works', 'pricing', 'faq'] as const;
type SectionId = typeof SECTIONS[number];

export default function Header() {
  const locale = useLocale();
  const t = useTranslations('navigation');
  useHashScrollOnNavigation();
  const headerRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionId | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };

    checkAuth();

    // Listen for auth state changes
    const supabase = createClient();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Scroll detection for header background and mobile nav visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 20);

      // Hide mobile nav while actively scrolling
      setIsScrolling(true);

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Show mobile nav after scroll stops (150ms delay)
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Intersection Observer for active section detection
  useEffect(() => {
    const observerCallback: IntersectionObserverCallback = (entries) => {
      // Find the most visible section
      let mostVisibleId: SectionId | null = null;
      let highestRatio = 0;

      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > highestRatio) {
          mostVisibleId = entry.target.id as SectionId;
          highestRatio = entry.intersectionRatio;
        }
      });

      if (mostVisibleId) {
        setActiveSection(mostVisibleId);
      }
    };

    const observer = new IntersectionObserver(observerCallback, {
      rootMargin: '-20% 0px -70% 0px', // Active when section is in upper portion of viewport
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });

    // Observe all sections
    SECTIONS.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className={`${styles.headerWrapper} ${isScrolling ? styles.scrolling : ''}`}>
      <header
        ref={headerRef}
        className={`${styles.header} ${isScrolled ? styles.scrolled : ''} ${isHovered ? styles.hovered : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Logo - Desktop */}
        <Link href={`/${locale}`} className={`${styles.logoLink} ${styles.logoLinkDesktop}`} aria-label="Interface Audit - Home">
          <Logo variant="desktop" className={styles.logo} />
        </Link>

        {/* Logo - Tablet/Mobile */}
        <Link href={`/${locale}`} className={`${styles.logoLink} ${styles.logoLinkMobile}`} aria-label="Interface Audit - Home">
          <Logo variant="mobile" className={styles.logo} />
        </Link>

        {/* Navigation - Visible on all screen sizes */}
        <nav id="main-navigation" className={styles.nav} aria-label={t('mainNavigation')}>
          <div className={styles.navGroup}>
            <Link
              href={`/${locale}/audit`}
              className={styles.navLink}
            >
              {t('freeAudit')}
            </Link>
            <div className={styles.divider} aria-hidden="true" />
            <Link
              href={`/${locale}/chat`}
              className={styles.navLink}
            >
              {t('chat')}
            </Link>
            <div className={styles.divider} aria-hidden="true" />
            <Link
              href={`/${locale}#pricing`}
              className={`${styles.navLink} ${activeSection === 'pricing' ? styles.active : ''}`}
              aria-current={activeSection === 'pricing' ? 'true' : undefined}
            >
              {t('pricing')}
            </Link>
            <div className={styles.divider} aria-hidden="true" />
            <Link
              href={`/${locale}#faq`}
              className={`${styles.navLink} ${activeSection === 'faq' ? styles.active : ''}`}
              aria-current={activeSection === 'faq' ? 'true' : undefined}
            >
              {t('faq')}
            </Link>
          </div>
        </nav>

        {/* Actions - Dashboard/Sign In + Language Selector */}
        <div className={styles.actions}>
          {isAuthenticated ? (
            <Link href={`/${locale}/dashboard`} className={styles.dashboardLink}>
              <LayoutDashboard size={18} />
              <span>{t('dashboard')}</span>
            </Link>
          ) : (
            <Link href={`/${locale}/sign-in`} className={styles.signInLink}>
              {t('signIn')}
            </Link>
          )}
          <LanguageSelector variant="compact" />
        </div>
      </header>
    </div>
  );
}
